import React, { useEffect, useState } from 'react';
import { Download, Printer, FileImage, FileCode } from 'lucide-react';
import { getProducts } from '../../lib/api';

// Slug -> printable label name + dose (mirrors generate_print_labels.py)
const LABEL_INDEX = {
  'reta-10mg':            { name: 'RETA',            dose: '10mg' },
  'ghk-50mg':             { name: 'GHK',             dose: '50mg' },
  'ghk-100mg':            { name: 'GHK',             dose: '100mg' },
  'glow-70mg':            { name: 'GLOW',            dose: '70mg' },
  'bpc-157-5mg':          { name: 'BPC-157',         dose: '5mg' },
  'bpc-157-10mg':         { name: 'BPC-157',         dose: '10mg' },
  'tb-500-5mg':           { name: 'TB-500',          dose: '5mg' },
  'tb-500-10mg':          { name: 'TB-500',          dose: '10mg' },
  'hgh-24iu':             { name: 'HGH',             dose: '24 IU' },
  'wolverine-stack-10mg': { name: 'WOLVERINE STACK', dose: '10mg' },
  'pt-141-10mg':          { name: 'PT-141',          dose: '10mg' },
  'kisspeptin-10mg':      { name: 'KISSPEPTIN',      dose: '10mg' },
  'klow-80mg':            { name: 'KLOW',            dose: '80mg' },
  'nad-1000mg':           { name: 'NAD+',            dose: '1000mg' },
  'semax-10mg':           { name: 'SEMAX',           dose: '10mg' },
  'mots-c-10mg':          { name: 'MOTS-c',          dose: '10mg' },
  'tesamorelin-10mg':     { name: 'TESAMORELIN',     dose: '10mg' },
  'snap-8-10mg':          { name: 'SNAP-8',          dose: '10mg' },
  'dsip-10mg':            { name: 'DSIP',            dose: '10mg' },
  'lipo-d-10mg':          { name: 'LIPO D',          dose: '10mg' },
  'b12-10mg':             { name: 'B12',             dose: '10mg' },
  'selank-10mg':          { name: 'SELANK',          dose: '10mg' },
  'ipamorelin-10mg':      { name: 'IPAMORELIN',      dose: '10mg' },
  'kpv-10mg':             { name: 'KPV',             dose: '10mg' },
  'fox04-dri-10mg':       { name: 'FOX04 DRI',       dose: '10mg' },
  'agh-cu-10mg':          { name: 'AGH CU',          dose: '10mg' },
};

const FORMATS = [
  {
    key: 'portrait',
    title: 'Portrait (full label)',
    sub: '600 × 800 vector / 2400 × 3200 raster — best for inserts, brochures, full-size print',
    fileBase: (slug) => `label-${slug}`,
    aspect: '3 / 4',
  },
  {
    key: 'vial',
    title: 'Vial — dark (3 ml vial)',
    sub: 'Trim 60 × 25 mm + 4 mm bleed on all sides → final file is 68 × 33 mm at 300 DPI (803 × 390 px). Silver on black — best on coated/foil stock.',
    fileBase: (slug) => `label-${slug}-vial`,
    aspect: '68 / 33',
  },
  {
    key: 'vial-white',
    title: 'Vial — white (3 ml vial)',
    sub: 'Trim 60 × 25 mm + 4 mm bleed on all sides → final file is 68 × 33 mm at 300 DPI (803 × 390 px). Black on white — ideal for plain vinyl / paper.',
    fileBase: (slug) => `label-${slug}-vial-white`,
    aspect: '68 / 33',
  },
];

const AdminLabels = () => {
  const [products, setProducts] = useState([]);
  const [format, setFormat] = useState('portrait');

  useEffect(() => {
    getProducts({}).then(setProducts).catch(() => setProducts([]));
  }, []);

  const labels = products.filter((p) => LABEL_INDEX[p.slug]);
  const active = FORMATS.find((f) => f.key === format);

  return (
    <div className="p-8 max-w-[1100px]">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
          <Printer className="text-[#909090]" /> Print-ready labels
        </h1>
        <p className="text-zinc-400 text-sm leading-relaxed max-w-3xl">
          High-resolution vial labels for every peptide in your catalog. <strong className="text-white">SVG</strong> files
          are vector (scale to any size, perfect for a print shop). <strong className="text-white">PNG</strong> files are raster.
          Pick a format below or grab everything in one ZIP.
        </p>
      </div>

      <a
        href="/downloads/labels/hydro-health-print-labels.zip"
        download
        data-testid="download-all-labels-zip"
        className="inline-flex items-center gap-2 bg-[#909090] hover:bg-[#a8a8a8] text-black font-semibold px-5 py-3 rounded transition-colors mb-6"
      >
        <Download size={18}/> Download all labels (.zip) — both formats
      </a>

      {/* Format tabs */}
      <div className="flex gap-2 border-b border-zinc-900 mb-6">
        {FORMATS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFormat(f.key)}
            data-testid={`label-format-${f.key}`}
            className={`px-4 py-2.5 text-sm font-semibold transition-colors border-b-2 -mb-px ${
              format === f.key
                ? 'text-[#909090] border-[#909090]'
                : 'text-zinc-500 border-transparent hover:text-white'
            }`}
          >
            {f.title}
          </button>
        ))}
      </div>
      <p className="text-zinc-500 text-xs mb-6">{active.sub}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {labels.map((p) => {
          const meta = LABEL_INDEX[p.slug];
          const base = active.fileBase(p.slug);
          const png = `/downloads/labels/${base}.png`;
          const svg = `/downloads/labels/${base}.svg`;
          return (
            <div
              key={p.slug + format}
              data-testid={`label-card-${p.slug}`}
              className="bg-zinc-950 border border-zinc-900 rounded p-4 hover:border-[#909090]/40 transition-colors"
            >
              <div
                className="bg-black rounded mb-3 overflow-hidden border border-zinc-900 flex items-center justify-center"
                style={{ aspectRatio: active.aspect }}
              >
                <img src={png} alt={`${meta.name} ${meta.dose} label`} loading="lazy" className="max-w-full max-h-full object-contain" />
              </div>
              <p className="text-white font-semibold text-sm">{meta.name} {meta.dose}</p>
              <p className="text-zinc-500 text-xs mb-3 truncate">/downloads/labels/{base}</p>
              <div className="flex gap-2">
                <a
                  href={svg}
                  download
                  data-testid={`download-svg-${p.slug}-${format}`}
                  className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold uppercase tracking-wider border border-[#909090] text-[#909090] hover:bg-[#909090] hover:text-black px-2 py-2 rounded transition-colors"
                >
                  <FileCode size={13}/> SVG
                </a>
                <a
                  href={png}
                  download
                  data-testid={`download-png-${p.slug}-${format}`}
                  className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold uppercase tracking-wider border border-zinc-700 text-zinc-300 hover:border-white hover:text-white px-2 py-2 rounded transition-colors"
                >
                  <FileImage size={13}/> PNG
                </a>
              </div>
            </div>
          );
        })}
      </div>

      {labels.length === 0 && (
        <p className="text-zinc-500 text-sm">No labels found.</p>
      )}
    </div>
  );
};

export default AdminLabels;
